import { supabase } from '../supabaseClient';
import { SessionType, AthleteSettings, TrainingPlan, TrainingWeek, WeeklySession, AllZones, PlanGoal, TrainingMetrics, WeeklyMetrics } from '../types/athlete';
import { calculateAllZones } from '../lib/zones/zoneCalculations';

// Session Types
export const getSessionTypes = async (discipline?: string): Promise<SessionType[]> => {
  let query = supabase.from('session_types').select('*').order('name');
  if (discipline) {
    query = query.eq('discipline', discipline);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const getSessionTypeById = async (id: string): Promise<SessionType | null> => {
  const { data, error } = await supabase.from('session_types').select('*').eq('id', id).single();
  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

// Athlete Settings
export const getAthleteSettings = async (userId: string): Promise<AthleteSettings | null> => {
  const { data, error } = await supabase.from('athlete_settings').select('*').eq('id', userId).single();
  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

export const saveAthleteSettings = async (userId: string, settings: Partial<AthleteSettings>): Promise<AthleteSettings> => {
  const { data, error } = await supabase
    .from('athlete_settings')
    .upsert({ id: userId, ...settings, updated_at: new Date().toISOString() })
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deleteAthleteSettings = async (userId: string): Promise<void> => {
  const { error } = await supabase.from('athlete_settings').delete().eq('id', userId);
  if (error) throw error;
};

export const getAllZones = async (userId: string): Promise<AllZones> => {
  const settings = await getAthleteSettings(userId);
  if (!settings) {
    return { run: [], swim: [], bike: [], hr: [] };
  }
  return calculateAllZones(settings);
};

// Training Plans
export const getTrainingPlans = async (userId: string): Promise<TrainingPlan[]> => {
  const { data, error } = await supabase
    .from('training_plans')
    .select('*')
    .eq('user_id', userId)
    .order('start_date', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const getTrainingPlan = async (userId: string, planId: string): Promise<TrainingPlan | null> => {
  const { data, error } = await supabase
    .from('training_plans')
    .select('*')
    .eq('id', planId)
    .eq('user_id', userId)
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

export const createTrainingPlan = async (
  userId: string,
  plan: { name: string; description?: string; start_date: string; end_date: string }
): Promise<TrainingPlan> => {
  const { data, error } = await supabase
    .from('training_plans')
    .insert([{ user_id: userId, ...plan }])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const updateTrainingPlan = async (
  userId: string,
  planId: string,
  updates: Partial<TrainingPlan>
): Promise<TrainingPlan> => {
  const { data, error } = await supabase
    .from('training_plans')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', planId)
    .eq('user_id', userId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deleteTrainingPlan = async (userId: string, planId: string): Promise<void> => {
  const { error } = await supabase.from('training_plans').delete().eq('id', planId).eq('user_id', userId);
  if (error) throw error;
};

// Training Weeks
export const getTrainingWeeks = async (userId: string, planId: string): Promise<TrainingWeek[]> => {
  const { data, error } = await supabase
    .from('training_weeks')
    .select('*')
    .eq('plan_id', planId)
    .order('week_number');
  if (error) throw error;
  return data || [];
};

export const createTrainingWeek = async (
  week: { plan_id: string; week_number: number; start_date: string; end_date: string; notes?: string }
): Promise<TrainingWeek> => {
  const { data, error } = await supabase.from('training_weeks').insert([week]).select().single();
  if (error) throw error;
  return data;
};

// Weekly Sessions
export const getWeeklySessions = async (userId: string, weekId: string): Promise<WeeklySession[]> => {
  const { data, error } = await supabase
    .from('weekly_sessions')
    .select(`
      *,
      session_type:session_types(*)
    `)
    .eq('week_id', weekId)
    .order('day_of_week');
  if (error) throw error;
  return data || [];
};

export const createWeeklySession = async (
  session: Omit<WeeklySession, 'id' | 'created_at' | 'session_type'>
): Promise<WeeklySession> => {
  const { data, error } = await supabase.from('weekly_sessions').insert([session]).select().single();
  if (error) throw error;
  return data;
};

export const updateWeeklySession = async (
  userId: string,
  sessionId: string,
  updates: Partial<WeeklySession>
): Promise<WeeklySession> => {
  const { data, error } = await supabase
    .from('weekly_sessions')
    .update(updates)
    .eq('id', sessionId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deleteWeeklySession = async (sessionId: string): Promise<void> => {
  const { error } = await supabase.from('weekly_sessions').delete().eq('id', sessionId);
  if (error) throw error;
};

// Plan Wizard Helper - Create full plan with weeks and sessions
export const createPlanWithWeeks = async (
  userId: string,
  planData: { name: string; description?: string; start_date: string; end_date: string },
  sessions: Array<{
    week_number: number;
    day_of_week: number;
    session_type_id: string;
    scheduled_date: string;
    planned_duration_minutes: number;
  }>
): Promise<TrainingPlan> => {
  // Create the plan
  const plan = await createTrainingPlan(userId, planData);

  // Group sessions by week
  const sessionsByWeek = sessions.reduce((acc, session) => {
    if (!acc[session.week_number]) {
      acc[session.week_number] = [];
    }
    acc[session.week_number].push(session);
    return acc;
  }, {} as Record<number, typeof sessions>);

  // Create weeks and sessions
  for (const [weekNum, weekSessions] of Object.entries(sessionsByWeek)) {
    const startDate = new Date(planData.start_date);
    startDate.setDate(startDate.getDate() + (parseInt(weekNum) - 1) * 7);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);

    const week = await createTrainingWeek({
      plan_id: plan.id,
      week_number: parseInt(weekNum),
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
    });

    for (const session of weekSessions) {
      await createWeeklySession({
        week_id: week.id,
        session_type_id: session.session_type_id,
        day_of_week: session.day_of_week,
        scheduled_date: session.scheduled_date,
        planned_duration_minutes: session.planned_duration_minutes,
      });
    }
  }

  return plan;
};

// ============================================
// PHASE 1: SESSION COMPLETION
// ============================================

export const completeWeeklySession = async (
  sessionId: string,
  data: {
    completed: boolean;
    actual_duration_minutes?: number;
    felt_rating?: number;
    completion_notes?: string;
  }
): Promise<WeeklySession> => {
  const updates: Partial<WeeklySession> = {
    completed: data.completed,
    completed_at: data.completed ? new Date().toISOString() : null,
    actual_duration_minutes: data.actual_duration_minutes ?? null,
    felt_rating: data.felt_rating ?? null,
    completion_notes: data.completion_notes ?? null,
  };

  const { data: session, error } = await supabase
    .from('weekly_sessions')
    .update(updates)
    .eq('id', sessionId)
    .select(`
      *,
      session_type:session_types(*)
    `)
    .single();

  if (error) throw error;
  return session;
};

export const getCompletedSessions = async (
  userId: string,
  startDate?: string,
  endDate?: string
): Promise<WeeklySession[]> => {
  let query = supabase
    .from('weekly_sessions')
    .select(`
      *,
      session_type:session_types(*),
      week:training_weeks(
        plan:training_plans(id, name)
      )
    `)
    .eq('completed', true)
    .order('completed_at', { ascending: false });

  if (startDate) {
    query = query.gte('scheduled_date', startDate);
  }
  if (endDate) {
    query = query.lte('scheduled_date', endDate);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// ============================================
// PHASE 4: PLAN GOALS
// ============================================

export const getPlanGoals = async (planId: string): Promise<PlanGoal[]> => {
  const { data, error } = await supabase
    .from('plan_goals')
    .select('*')
    .eq('plan_id', planId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const createPlanGoal = async (
  goal: Omit<PlanGoal, 'id' | 'progress_current' | 'created_at'>
): Promise<PlanGoal> => {
  const { data, error } = await supabase
    .from('plan_goals')
    .insert([goal])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updatePlanGoal = async (
  goalId: string,
  updates: Partial<PlanGoal>
): Promise<PlanGoal> => {
  const { data, error } = await supabase
    .from('plan_goals')
    .update(updates)
    .eq('id', goalId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deletePlanGoal = async (goalId: string): Promise<void> => {
  const { error } = await supabase.from('plan_goals').delete().eq('id', goalId);
  if (error) throw error;
};

export const incrementGoalProgress = async (
  goalId: string,
  incrementBy: number
): Promise<PlanGoal> => {
  const { data: goal, error: fetchError } = await supabase
    .from('plan_goals')
    .select('progress_current')
    .eq('id', goalId)
    .single();

  if (fetchError) throw fetchError;

  return updatePlanGoal(goalId, {
    progress_current: (goal.progress_current || 0) + incrementBy,
  });
};

// ============================================
// PHASE 5: TRAINING METRICS
// ============================================

const getWeekBounds = (date: Date): { start: Date; end: Date } => {
  const day = date.getDay();
  const start = new Date(date);
  start.setDate(date.getDate() - day + 1);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
};

export const getTrainingMetrics = async (
  userId: string,
  startDate?: string,
  endDate?: string
): Promise<TrainingMetrics> => {
  const today = new Date();
  const weekBounds = getWeekBounds(today);

  const weekStart = startDate || weekBounds.start.toISOString().split('T')[0];
  const weekEnd = endDate || weekBounds.end.toISOString().split('T')[0];

  // Get all plans for user (include both active and draft plans)
  const plans = await getTrainingPlans(userId);
  const activePlans = plans.filter(p => p.status === 'active' || p.status === 'draft');

  // Get all weeks for active plans
  let allWeeks: TrainingWeek[] = [];
  let allSessions: WeeklySession[] = [];

  for (const plan of activePlans) {
    const weeks = await getTrainingWeeks(userId, plan.id);
    allWeeks = [...allWeeks, ...weeks];
  }

  // Get all sessions for these weeks
  for (const week of allWeeks) {
    const sessions = await getWeeklySessions(userId, week.id);
    allSessions = [...allSessions, ...sessions];
  }

  // Filter to this week
  const thisWeekSessions = allSessions.filter(s => {
    if (!s.scheduled_date) return false;
    return s.scheduled_date >= weekStart && s.scheduled_date <= weekEnd;
  });

  // Calculate weekly metrics
  const completedSessions = thisWeekSessions.filter(s => s.completed);
  const totalPlannedMinutes = thisWeekSessions.reduce((sum, s) => sum + (s.planned_duration_minutes || 0), 0);
  const totalActualMinutes = completedSessions.reduce((sum, s) => sum + (s.actual_duration_minutes || 0), 0);

  const sessionsByDiscipline = { swim: 0, bike: 0, run: 0, strength: 0 };
  const actualByDiscipline = { swim: 0, bike: 0, run: 0, strength: 0 };

  thisWeekSessions.forEach(s => {
    const discipline = s.session_type?.discipline || 'strength';
    if (discipline in sessionsByDiscipline) {
      sessionsByDiscipline[discipline as keyof typeof sessionsByDiscipline]++;
    }
  });

  completedSessions.forEach(s => {
    const discipline = s.session_type?.discipline || 'strength';
    if (discipline in actualByDiscipline) {
      actualByDiscipline[discipline as keyof typeof actualByDiscipline] += s.actual_duration_minutes || 0;
    }
  });

  const thisWeekMetrics: WeeklyMetrics = {
    weekStartDate: weekStart,
    weekEndDate: weekEnd,
    totalSessions: thisWeekSessions.length,
    completedSessions: completedSessions.length,
    totalPlannedMinutes,
    totalActualMinutes,
    sessionsByDiscipline,
    actualByDiscipline,
    completionRate: thisWeekSessions.length > 0
      ? Math.round((completedSessions.length / thisWeekSessions.length) * 100)
      : 0,
    streak: 0, // TODO: Calculate from completed sessions
  };

  // Get upcoming sessions (next 7 days)
  const nextWeekEnd = new Date(today);
  nextWeekEnd.setDate(today.getDate() + 7);
  const upcomingSessions = allSessions
    .filter(s => {
      if (!s.scheduled_date) return false;
      const scheduled = new Date(s.scheduled_date);
      return scheduled > today && scheduled <= nextWeekEnd && !s.completed;
    })
    .slice(0, 5)
    .map(s => ({
      id: s.id,
      scheduled_date: s.scheduled_date!,
      session_name: s.session_type?.name || 'Unknown Session',
      planned_duration: s.planned_duration_minutes || 0,
      plan_name: plans.find(p => p.id === allWeeks.find(w => w.id === s.week_id)?.plan_id)?.name || 'Unknown',
    }));

  // Get this month stats
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

  const thisMonthSessions = allSessions.filter(s => {
    if (!s.scheduled_date) return false;
    return s.scheduled_date >= monthStart && s.scheduled_date <= monthEnd;
  });

  const thisMonthMinutes = thisMonthSessions.reduce((sum, s) => sum + (s.actual_duration_minutes || s.planned_duration_minutes || 0), 0);
  const avgPerSession = thisMonthSessions.length > 0 ? Math.round(thisMonthMinutes / thisMonthSessions.length) : 0;

  const disciplineBreakdown = ['swim', 'bike', 'run', 'strength'].map(d => {
    const count = thisMonthSessions.filter(s => s.session_type?.discipline === d).length;
    const minutes = thisMonthSessions
      .filter(s => s.session_type?.discipline === d)
      .reduce((sum, s) => sum + (s.actual_duration_minutes || s.planned_duration_minutes || 0), 0);
    return { discipline: d, count, minutes };
  });

  // Get goals progress
  let goalsProgress: { goal: PlanGoal; progressPercent: number; remaining: number }[] = [];
  for (const plan of activePlans) {
    const goals = await getPlanGoals(plan.id);
    for (const goal of goals) {
      const progressPercent = goal.target_value > 0
        ? Math.round((goal.progress_current / goal.target_value) * 100)
        : 0;
      const remaining = Math.max(0, goal.target_value - goal.progress_current);
      goalsProgress.push({ goal, progressPercent, remaining });
    }
  }

  return {
    thisWeek: thisWeekMetrics,
    thisMonth: {
      totalSessions: thisMonthSessions.length,
      totalMinutes: thisMonthMinutes,
      averagePerSession: avgPerSession,
      disciplineBreakdown,
    },
    upcomingSessions,
    goalsProgress,
  };
};

// ============================================
// HELPER: AUTO-UPDATE GOALS ON SESSION COMPLETION
// ============================================

export const updateGoalsOnSessionComplete = async (
  session: WeeklySession,
  isCompleting: boolean
): Promise<void> => {
  // Get the week and plan for this session
  const { data: week, error: weekError } = await supabase
    .from('training_weeks')
    .select('plan_id')
    .eq('id', session.week_id)
    .single();

  if (weekError || !week) return;

  const goals = await getPlanGoals(week.plan_id);
  const discipline = session.session_type?.discipline;

  if (!discipline) return;

  for (const goal of goals) {
    if (goal.discipline !== discipline) continue;

    let incrementBy = 0;

    if (goal.target_type === 'sessions') {
      incrementBy = isCompleting ? 1 : -1;
    } else if (goal.target_type === 'time') {
      const duration = isCompleting
        ? (session.actual_duration_minutes || session.planned_duration_minutes || 0)
        : -(session.actual_duration_minutes || session.planned_duration_minutes || 0);
      incrementBy = duration;
    } else if (goal.target_type === 'distance') {
      // For distance goals, estimate from session type
      // This is a simplified estimation
      const estimatedDistance = estimateSessionDistance(discipline, session.planned_duration_minutes || 0);
      incrementBy = isCompleting ? estimatedDistance : -estimatedDistance;
    }

    if (incrementBy !== 0) {
      await incrementGoalProgress(goal.id, incrementBy);
    }
  }
};

const estimateSessionDistance = (
  discipline: string,
  durationMinutes: number
): number => {
  // Rough estimates in km
  const pacePerKm: Record<string, number> = {
    swim: 2.5, // minutes per km (CSS pace ~2min/100m)
    bike: 2.5, // km per minute at moderate pace
    run: 6,    // minutes per km
  };

  const pace = pacePerKm[discipline] || 6;
  return durationMinutes / pace;
};
